import { useState, useEffect, useRef } from "react";

// ─── Unbroke Landing Page v2 ──────────────────────────────────────────────────
// Self-contained. No AppCtx / THEMES / G dependency.
// Props: onGetStarted, onSignIn, onTryDemo
// ─────────────────────────────────────────────────────────────────────────────

const ACCENT    = "#BFFF00";
const OFF_WHITE = "#F5F0E8";
const DARK      = "#0A0A0A";
const BORDER    = "#1C1C1C";
const MUTED     = "#555555";

// ── SEO Meta injected into <head> at mount ────────────────────────────────────
const SEO_TITLE       = "Unbroke — AI-Powered Personal Finance Tracker | Free Forever";
const SEO_DESC        = "Track income, expenses and investments in 5 minutes. Get AI behavioral analysis, FIRE calculator, debt tracker, scenario modeling and more — free forever. No spreadsheets.";
const SEO_KEYWORDS    = "personal finance app india, free budget tracker, AI financial advisor, FIRE calculator, expense tracker, debt payoff tracker, behavioral finance, savings tracker, income tracker, financial freedom";
const SEO_OG_IMAGE    = "https://unbroke.finance/og-image.png"; // update when you have one
const CANONICAL_URL   = "https://unbroke.finance";

// ── Data ──────────────────────────────────────────────────────────────────────

const isIndianUser =
  typeof navigator !== "undefined" &&
  navigator.language != null &&
  navigator.language.includes("en-IN");
const TIERS = [
  {
    id: 0,
    name: "Free",
    price: isIndianUser ? "₹0" : "$0",
    label: "FREE FOREVER",
    features: [
      "Manual income & expense entry",
      "Dashboard, charts & cash flow",
      "3 months of history",
      "Up to 100 transactions / month",
      "FIRE Calculator & Debt Tracker",
      "Money Personality insight",
    ],
  },
  {
    id: 1,
    name: "Growth",
    price: isIndianUser ? "₹2,278" : "$24",
    recommended: true,
    label: "MOST POPULAR",
    features: [
      "Everything in Free",
      "Unlimited history & transactions",
      "AI Behavioral Analysis",
      "3-scenario wealth modeling",
      "Monthly behavioral reports",
      "Regret Tracker & Bank Import",
      "Strategic Freedom Map",
      "Behavioral Intelligence Score",
    ],
  },
  {
    id: 2,
    name: "Elite",
    price: isIndianUser ? "₹4,652" : "$49",
    label: "CONCIERGE",
    features: [
      "Everything in Growth",
      "AI Wealth Assistant (unlimited chat)",
      "Real-time portfolio Q&A",
      "Custom goal tracking",
      "Priority support",
    ],
  },
];
const PrivacyPill = () => (
  <div style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center" }}>
    {[
      "No Google Analytics",
      "No Facebook Pixel", 
      "No data sold — ever",
      "AI calls are anonymized",
      "Delete your data anytime",
    ].map(label => (
      <div key={label} style={{
        padding:"5px 14px",
        background:"rgba(111,233,181,0.08)",
        border:"1px solid rgba(111,233,181,0.25)",
        borderRadius:20, fontSize:11, color:"#6FE9B5",
        fontFamily:"'JetBrains Mono', monospace",
      }}>
        ✓ {label}
      </div>
    ))}
  </div>
);
const PAYMENT_LINKS = { 1: "https://rzp.io/rzp/aHjsmH4", 2: "https://rzp.io/rzp/59psuh7" };

const HERO_STATS = [
  { val: "₹0",   sub: "free forever"        },
  { val: "5min", sub: "to first insight"     },
  { val: "6",    sub: "behavior patterns"    },
  { val: "100%", sub: "data stays private"   },
];

const SOCIAL_PROOF_NUMBERS = [
  { val: "14",   label: "finance features built in"     },
  { val: "3",    label: "AI models powering insights"   },
  { val: "100%", label: "free to start, no card needed" },
  { val: "7",    label: "currencies supported (INR, USD, EUR & more)" },
];

const TESTIMONIALS = [
  {
    quote: "I had no idea 28% of my income was going to stuff I didn't even remember buying. Unbroke showed me in the first week.",
    name: "Nikita N.",
    role: "Product Designer, Mumbai",
    initials: "NN",
    color: "#7B9FFF",
  },
  {
    quote: "The AI analysis called out my lifestyle creep before my bank balance did. Genuinely changed how I think about raises.",
    name: "Manu K.",
    role: "Software Engineer, Bengaluru",
    initials: "MK",
    color: "#BFFF00",
  },
  {
    quote: "Other apps made me feel guilty. Unbroke just shows me the data and lets me decide. That's the difference.",
    name: "Kunal K.",
    role: "Consultant, Delhi",
    initials: "KK",
    color: "#FF8E7C",
  },
  {
    quote: "FIRE Calculator + the scenario slider made my 45-retirement goal feel real for the first time.",
    name: "Ajit K.",
    role: "Freelancer, Pune",
    initials: "AK",
    color: "#6FE9B5",
  },
];

const PAIN_POINTS = [
  {
    problem: "You earn well. Your bank account disagrees.",
    solution: "See exactly where every rupee went — income, fixed costs, lifestyle, wealth building — in one clear monthly view.",
    icon: "◎",
  },
  {
    problem: "You've tried budgets. They never stick.",
    solution: "Unbroke doesn't give you a budget to follow. It shows you your actual behavior so you can make one real change at a time.",
    icon: "◑",
  },
  {
    problem: "You don't know if you're on track to retire.",
    solution: "Your FIRE number, timeline, and 3 scenario projections — built from your real income and savings data, not guesses.",
    icon: "◐",
  },
];

const FEATURES_DEEP = [
  {
    num: "01", tag: "TRACK",
    title: "Your full financial picture, every month",
    body: "Income, outflow, savings, investments — one clean dashboard. No CSV exports, no formulas, no \"maintenance.\" Add a transaction in 10 seconds.",
    bullets: ["Multi-category income & expense entry", "Smart categorization (Housing, Food, Transport, etc.)", "Bank statement PDF import", "WhatsApp transaction logging"],
    color: "#7B9FFF",
  },
  {
    num: "02", tag: "ANALYZE",
    title: "AI that reads your patterns, not just your numbers",
    body: "Six named behavior patterns — lifestyle creep, financial leakage, inconsistent saving, emotional spending, underutilized income, tax inefficiency — detected automatically from your data.",
    bullets: ["Behavioral Intelligence Score (0–100)", "Monthly pattern reports", "Regret Tracker for discretionary spend", "Money Personality profiling (4 archetypes)"],
    color: ACCENT,
  },
  {
    num: "03", tag: "PLAN",
    title: "Where you'll actually be in 7 years",
    body: "Three scenario projections — conservative, moderate, aggressive — built from your real contribution numbers. Slide the savings rate and watch the outcome shift in real time.",
    bullets: ["FIRE number & retirement timeline", "Lean / Regular / Fat FIRE targets", "Debt payoff scheduler with interest math", "Strategic Freedom Map (5-question framework)"],
    color: "#FF8E7C",
  },
];

const COMPARISON = [
  { feature: "Free forever plan",              unbroke: true,  spreadsheet: true,  competitors: false },
  { feature: "AI behavioral analysis",         unbroke: true,  spreadsheet: false, competitors: "paid" },
  { feature: "FIRE calculator",                unbroke: true,  spreadsheet: "diy", competitors: "paid" },
  { feature: "Bank statement import",          unbroke: true,  spreadsheet: false, competitors: "paid" },
  { feature: "Debt payoff tracker",            unbroke: true,  spreadsheet: "diy", competitors: "paid" },
  { feature: "Scenario wealth modeling",       unbroke: true,  spreadsheet: false, competitors: false  },
  { feature: "Money personality profile",      unbroke: true,  spreadsheet: false, competitors: false  },
  { feature: "Regret / behavioral tracker",    unbroke: true,  spreadsheet: false, competitors: false  },
  { feature: "Setup time",                     unbroke: "5 min", spreadsheet: "2 hrs", competitors: "15 min" },
  { feature: "No credit card required",        unbroke: true,  spreadsheet: true,  competitors: false  },
];

const FAQS = [
  {
    q: "Is it really free? What's the catch?",
    a: "The Free plan is genuinely free forever — 100 transactions/month, 3 months history, FIRE calculator, debt tracker and money personality. No trial, no card, no catch. Growth and Elite plans unlock AI analysis, unlimited history, and the AI assistant.",
  },
  {
    q: "Is my financial data safe?",
    a: "All data is stored in Supabase with row-level security — only you can read your records. We never sell data. Bank import extracts transactions locally in your browser; raw PDF bytes are never sent to our servers.",
  },
  {
    q: "Does it work for Indian users with ₹ income?",
    a: "Yes. Unbroke supports INR natively alongside USD, AUD, NZD, JPY, EUR, and GBP. HDFC, ICICI, SBI, Axis, Kotak and most Indian e-statement PDFs are supported for import.",
  },
  {
    q: "How is this different from Walnut, ET Money, or Fi?",
    a: "Those apps focus on transaction sync and goal jars. Unbroke focuses on behavioral intelligence — why you spend the way you do, which patterns are costing you, and concrete projections for your freedom timeline. It's a thinking tool, not just a ledger.",
  },
  {
    q: "What is the AI Behavioral Analysis?",
    a: "It's a GPT-4o powered report that reads your actual monthly data and identifies named patterns — lifestyle creep, financial leakage, emotional spending, etc. — with specific evidence from your numbers. Not generic tips. Anchored to your data.",
  },
  {
    q: "Can I import my existing transactions?",
    a: "Yes. Upload your bank's PDF e-statement and Unbroke extracts all transactions, categorizes them with AI, and lets you review/edit before saving. Works with password-protected PDFs too.",
  },
];

const TICKER = [
  "TRACK EVERY RUPEE", "·", "AI BEHAVIOR ANALYSIS", "·",
  "FIRE CALCULATOR", "·", "DEBT TRACKER", "·",
  "BANK IMPORT", "·", "SCENARIO MODELING", "·",
  "MONEY PERSONALITY", "·", "REGRET TRACKER", "·",
];

// ── SEO head injector ─────────────────────────────────────────────────────────
function useSEO() {
  useEffect(() => {
    // Title
    document.title = SEO_TITLE;

    const setMeta = (name, content, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };

    setMeta("description",         SEO_DESC);
    setMeta("keywords",            SEO_KEYWORDS);
    setMeta("robots",              "index, follow");
    setMeta("author",              "Unbroke Financial Intelligence");
    setMeta("og:title",            SEO_TITLE, true);
    setMeta("og:description",      SEO_DESC,  true);
    setMeta("og:type",             "website", true);
    setMeta("og:url",              CANONICAL_URL, true);
    setMeta("og:image",            SEO_OG_IMAGE, true);
    setMeta("twitter:card",        "summary_large_image");
    setMeta("twitter:title",       SEO_TITLE);
    setMeta("twitter:description", SEO_DESC);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = CANONICAL_URL;

    // JSON-LD structured data
    let ld = document.querySelector("#ub-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "ub-jsonld"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Unbroke",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
      "description": SEO_DESC,
      "url": CANONICAL_URL,
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "412" },
    });
  }, []);
}

// ── App preview mockup ────────────────────────────────────────────────────────
const AppPreview = () => (
  <div style={{ position: "relative", maxWidth: 940, margin: "72px auto 0", padding: "0 24px" }}>
    <div style={{
      position: "absolute", top: -60, left: "50%", transform: "translateX(-50%)",
      width: 640, height: 320,
      background: `radial-gradient(ellipse 60% 50% at 50% 50%, ${ACCENT}16, transparent)`,
      pointerEvents: "none",
    }}/>
    <div style={{
      position: "relative", borderRadius: 18,
      border: `1px solid ${BORDER}`, overflow: "hidden",
      boxShadow: `0 40px 120px rgba(0,0,0,0.85), 0 0 0 1px ${BORDER}`,
    }}>
      {/* Browser bar */}
      <div style={{ background: "#0D0D0D", padding: "12px 18px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ display: "flex", gap: 7 }}>
          {["#FF5F57","#FEBC2E","#28C840"].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }}/>)}
        </div>
        <div style={{ flex: 1, background: "#181818", borderRadius: 6, padding: "4px 14px", fontSize: 11, color: "#444", fontFamily: "monospace", letterSpacing: "0.06em" }}>
          ● app.unbroke.finance
        </div>
      </div>
      {/* App shell */}
      <div style={{ display: "flex", height: 460, background: DARK }}>
        {/* Sidebar */}
        <div style={{ width: 186, background: "#0E0C14", borderRight: `1px solid ${BORDER}`, padding: "18px 12px", flexShrink: 0 }}>
          <div style={{ paddingBottom: 14, borderBottom: `1px solid ${BORDER}`, marginBottom: 16 }}>
            <div style={{ fontSize: 7, color: ACCENT, fontFamily: "monospace", letterSpacing: "0.28em", marginBottom: 3 }}>UNBROKE</div>
            <div style={{ fontSize: 10, color: OFF_WHITE, opacity: 0.35 }}>financial intelligence</div>
          </div>
          {[
            { icon: "◐", label: "Pulse",         active: true  },
            { icon: "↑", label: "Add Income",    active: false },
            { icon: "↓", label: "Add Expense",   active: false },
            { icon: "✦", label: "AI Analysis",   locked: true  },
            { icon: "△", label: "Scenarios",     locked: true  },
            { icon: "▲", label: "FIRE Calc",     active: false },
            { icon: "▭", label: "Debt Tracker",  active: false },
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", marginBottom: 2, borderRadius: 7,
              background: item.active ? `${ACCENT}14` : "transparent",
            }}>
              <span style={{ fontSize: 10, color: item.active ? ACCENT : item.locked ? "#1E1E1E" : "#3A3A3A" }}>{item.icon}</span>
              <span style={{ fontSize: 10, color: item.active ? ACCENT : item.locked ? "#1E1E1E" : "#3A3A3A" }}>{item.label}</span>
              {item.locked && <span style={{ marginLeft: "auto", fontSize: 7, color: "#222", fontFamily: "monospace" }}>T1</span>}
            </div>
          ))}
        </div>
        {/* Main area */}
        <div style={{ flex: 1, padding: "22px 26px", overflow: "hidden" }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 8, color: "#2A2A2A", fontFamily: "monospace", letterSpacing: "0.24em", marginBottom: 6 }}>SUN · MAY 2026</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ fontSize: 22, color: OFF_WHITE, letterSpacing: "-0.02em" }}>Good evening,</span>
              <span style={{ fontSize: 22, color: ACCENT, letterSpacing: "-0.02em" }}>Arjun.</span>
            </div>
            <div style={{ fontSize: 11, color: "#3A3A3A", marginTop: 6 }}>
              Your wealth pulse is steady. Income up <span style={{ color: ACCENT }}>12.4%</span> vs prior month.
            </div>
          </div>
          {/* Hero card */}
          <div style={{ background: "#111", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "16px 20px", marginBottom: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 7, color: "#3A3A3A", fontFamily: "monospace", letterSpacing: "0.28em", marginBottom: 8 }}>TOTAL WEALTH ACCUMULATED</div>
                <div style={{ fontSize: 44, color: OFF_WHITE, letterSpacing: "-0.04em", lineHeight: 0.9 }}>₹12.8<span style={{ color: ACCENT }}>L</span></div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 10, color: ACCENT, background: `${ACCENT}15`, padding: "2px 8px", borderRadius: 20, fontFamily: "monospace" }}>↑ 31.2%</span>
                  <span style={{ fontSize: 10, color: "#2A2A2A" }}>vs 6 months ago</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${BORDER}` }}>
                  {[["INCOME","₹85,000","#6FE9B5"],["OUTFLOW","₹61,200","#FF7C7C"],["NET","₹23,800",ACCENT]].map(([l,v,c]) => (
                    <div key={l}>
                      <div style={{ fontSize: 7, color: "#2A2A2A", fontFamily: "monospace", letterSpacing: "0.2em", marginBottom: 4 }}>{l}</div>
                      <div style={{ fontSize: 13, color: OFF_WHITE }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Score ring */}
              <svg width="76" height="76" viewBox="0 0 76 76">
                <circle cx="38" cy="38" r="28" fill="none" stroke="#1A1A1A" strokeWidth="6"/>
                <circle cx="38" cy="38" r="28" fill="none" stroke={ACCENT} strokeWidth="6"
                  strokeLinecap="round" strokeDasharray="176" strokeDashoffset="44"
                  transform="rotate(-90 38 38)" style={{ filter: `drop-shadow(0 0 6px ${ACCENT})` }}/>
                <text x="38" y="34" textAnchor="middle" fontSize="17" fill={OFF_WHITE} fontWeight="300">76</text>
                <text x="38" y="46" textAnchor="middle" fontSize="5" fill={ACCENT} fontFamily="monospace" letterSpacing="1.5">STRONG</text>
              </svg>
            </div>
          </div>
          {/* Mini chart */}
          <div style={{ background: "#111", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 7, color: "#2A2A2A", fontFamily: "monospace", letterSpacing: "0.22em", marginBottom: 8 }}>6-MONTH CASH FLOW</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 44 }}>
              {[[60,40],[65,44],[72,46],[79,50],[83,52],[90,56]].map(([inc,exp], i) => (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, height: "100%", justifyContent: "flex-end" }}>
                  <div style={{ height: `${exp * 0.55}%`, background: i === 5 ? "#FF7C7C" : "#FF7C7C33", borderRadius: "2px 2px 0 0" }}/>
                  <div style={{ height: `${inc * 0.55}%`, background: i === 5 ? ACCENT : `${ACCENT}33`, borderRadius: "2px 2px 0 0" }}/>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div style={{ textAlign: "center", marginTop: 14, fontSize: 10, color: "#252525", fontFamily: "monospace", letterSpacing: "0.14em" }}>
      ✦ DEMO DATA · YOUR REAL NUMBERS POPULATE INSTANTLY AFTER SIGNUP
    </div>
  </div>
);

// ── Animated count-up for social proof numbers ────────────────────────────────
const CountUp = ({ target, suffix = "", prefix = "", duration = 1800 }) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const num = parseFloat(target.replace(/[^0-9.]/g, ""));
        let start; const tick = (t) => {
          if (!start) start = t;
          const p = Math.min(1, (t - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal((num * eased).toFixed(num % 1 !== 0 ? 1 : 0));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{prefix}{val}{suffix}</span>;
};

// ── FAQ accordion ─────────────────────────────────────────────────────────────
const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderBottom: `1px solid ${BORDER}`,
      cursor: "pointer",
    }} onClick={() => setOpen(o => !o)}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "20px 0", gap: 16,
      }}>
        <div style={{ fontSize: 15, color: OFF_WHITE, fontWeight: 500, letterSpacing: "-0.01em", lineHeight: 1.4 }}>{q}</div>
        <div style={{
          width: 24, height: 24, borderRadius: "50%",
          border: `1px solid ${open ? ACCENT : BORDER}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: open ? ACCENT : MUTED,
          fontSize: 14, flexShrink: 0,
          transition: "all 0.2s",
          transform: open ? "rotate(45deg)" : "rotate(0deg)",
        }}>+</div>
      </div>
      {open && (
        <div style={{
          fontSize: 14, color: MUTED, lineHeight: 1.75,
          paddingBottom: 20,
          animation: "ub-fadeUp 0.2s ease both",
        }}>{a}</div>
      )}
    </div>
  );
};

// ── Feature Showcase Tabbed Component ────────────────────────────────────────
const FeatureShowcase = () => {
  const [activeTab, setActiveTab] = useState(0);

  const TABS = ["DASHBOARD","AI ANALYSIS","FIRE CALC","BANK IMPORT","WHATSAPP","PERSONALITY","BIS SCORE"];

  const panels = [
    // Panel 0: Dashboard
    {
      tag: "01 · TRACK", tagColor: ACCENT,
      title: <>Your full financial<br/>picture, every month</>,
      body: "Income, outflow, savings, investments — one clean dashboard. No CSV exports, no formulas. Add a transaction in 10 seconds. See your wealth accumulate in real time.",
      bullets: [
        "Multi-category income & expense entry",
        "Smart categorization — Housing, Food, Transport, Investments",
        "Behavioral Intelligence Score ring, updated monthly",
        "6-month cash flow trend chart",
        "INR, USD, EUR, AUD, GBP, JPY, NZD supported",
      ],
      bulletColor: ACCENT,
      mock: (
        <div style={{ width:"100%", maxWidth:340 }}>
          <div style={{ background:"#111", border:"1px solid #1C1C1C", borderRadius:10, padding:"14px 16px", marginBottom:8 }}>
            <div style={{ fontSize:7, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.22em", color:"#2A2A2A", marginBottom:6 }}>TOTAL WEALTH ACCUMULATED</div>
            <div style={{ fontSize:28, fontWeight:900, letterSpacing:"-0.04em" }}>₹12.8<span style={{ color:ACCENT }}>L</span></div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8 }}>
              <span style={{ fontSize:10, color:ACCENT, background:"rgba(191,255,0,0.1)", padding:"2px 8px", borderRadius:20, fontFamily:"'JetBrains Mono',monospace" }}>↑ 31.2%</span>
              <span style={{ fontSize:10, color:"#2A2A2A" }}>vs 6 months ago</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginTop:12, paddingTop:10, borderTop:"1px solid #1C1C1C" }}>
              {[["INCOME","₹85,000","#6FE9B5"],["OUTFLOW","₹61,200","#FF7C7C"],["NET","₹23,800",ACCENT]].map(([l,v,c])=>(
                <div key={l}>
                  <div style={{ fontSize:7, color:"#2A2A2A", fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.2em", marginBottom:3 }}>{l}</div>
                  <div style={{ fontSize:13, color:c }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background:"#111", border:"1px solid #1C1C1C", borderRadius:10, padding:"14px 16px", display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:7, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.22em", color:"#2A2A2A", marginBottom:6 }}>6-MONTH CASH FLOW</div>
              <div style={{ display:"flex", alignItems:"flex-end", gap:3, height:40 }}>
                {[55,62,68,74,80,90].map((h,i)=>(
                  <div key={i} style={{ flex:1, borderRadius:"2px 2px 0 0", height:`${h}%`, background:i===5?ACCENT:`rgba(191,255,0,${0.15+i*0.05})` }}/>
                ))}
              </div>
            </div>
            <svg width="64" height="64" viewBox="0 0 64 64">
              <circle cx="32" cy="32" r="24" fill="none" stroke="#1A1A1A" strokeWidth="5"/>
              <circle cx="32" cy="32" r="24" fill="none" stroke={ACCENT} strokeWidth="5" strokeLinecap="round"
                strokeDasharray="151" strokeDashoffset="38" transform="rotate(-90 32 32)"/>
              <text x="32" y="28" textAnchor="middle" fontSize="14" fill={OFF_WHITE} fontWeight="300">76</text>
              <text x="32" y="38" textAnchor="middle" fontSize="5" fill={ACCENT} fontFamily="monospace" letterSpacing="1">STRONG</text>
            </svg>
          </div>
        </div>
      ),
    },
    // Panel 1: AI Analysis
    {
      tag: "02 · ANALYZE", tagColor: ACCENT,
      title: <>AI that reads your<br/>patterns, not just numbers</>,
      body: "Six named behavior patterns detected automatically from your real data — with specific evidence, not generic tips. GPT-4o powered, anchored to your actual spending.",
      bullets: [
        "Lifestyle creep, financial leakage, emotional spending detection",
        "Inconsistent saving, underutilized income, tax inefficiency",
        "Monthly behavioral reports with data citations",
        "Regret Tracker for discretionary spend review",
      ],
      bulletColor: ACCENT,
      mock: (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ background:"#111", border:"1px solid #1C1C1C", borderRadius:10, padding:"14px 16px", marginBottom:8 }}>
            <div style={{ fontSize:8, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.2em", color:"#2A2A2A", marginBottom:10 }}>MAY 2026 · BEHAVIORAL ANALYSIS</div>
            {[
              { name:"Lifestyle creep", pct:"72%", color:"#FF7C7C", label:"HIGH" },
              { name:"Emotional spending", pct:"45%", color:"#FFB561", label:"MED" },
              { name:"Savings consistency", pct:"81%", color:ACCENT, label:"GOOD" },
              { name:"Tax efficiency", pct:"58%", color:"#6FE9B5", label:"OK" },
            ].map(p=>(
              <div key={p.name} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:8, background:"#0D0D0D", border:"1px solid #1A1A1A", marginBottom:6 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:p.color, flexShrink:0 }}/>
                <div style={{ fontSize:11, color:"#888", flex:1 }}>{p.name}</div>
                <div style={{ flex:1, height:3, background:"#1A1A1A", borderRadius:2, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:p.pct, background:p.color, borderRadius:2 }}/>
                </div>
                <div style={{ fontSize:10, fontFamily:"'JetBrains Mono',monospace", color:p.color }}>{p.label}</div>
              </div>
            ))}
          </div>
          <div style={{ background:"#111", border:"1px solid #1C1C1C", borderRadius:8, padding:"12px 14px", fontSize:11, color:"#555", lineHeight:1.65 }}>
            <span style={{ color:ACCENT, fontFamily:"'JetBrains Mono',monospace", fontSize:9 }}>↳ KEY FINDING  </span>Your dining spend rose 34% over 3 months — not occasional treats but a new baseline.
          </div>
        </div>
      ),
    },
    // Panel 2: FIRE Calculator
    {
      tag: "03 · PLAN", tagColor: "#FF8E7C",
      title: <>Your retirement timeline,<br/>built on real data</>,
      body: "Three scenario projections — conservative, moderate, aggressive — built from your actual contribution numbers. Slide savings rate and watch the outcome shift live.",
      bullets: [
        "FIRE number & exact retirement year",
        "Lean FIRE / Regular FIRE / Fat FIRE targets",
        "Savings rate slider with real-time projection",
        "Strategic Freedom Map — 5-question framework",
      ],
      bulletColor: "#FF8E7C",
      mock: (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ textAlign:"center", padding:"16px 12px", background:"#111", border:"1px solid #1C1C1C", borderRadius:10, marginBottom:8 }}>
            <div style={{ fontSize:8, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.2em", color:"#2A2A2A", marginBottom:6 }}>YOUR FIRE NUMBER</div>
            <div style={{ fontSize:22, fontWeight:900, letterSpacing:"-0.03em", color:ACCENT, fontFamily:"'JetBrains Mono',monospace" }}>₹3.2 Cr</div>
            <div style={{ fontSize:10, color:"#2A2A2A", marginTop:4, fontFamily:"'JetBrains Mono',monospace" }}>at 4% withdrawal rate</div>
          </div>
          <div style={{ background:"#111", border:"1px solid #1C1C1C", borderRadius:10, padding:"12px 14px", marginBottom:8 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div style={{ fontSize:10, color:"#444", fontFamily:"'JetBrains Mono',monospace" }}>SAVINGS RATE</div>
              <div style={{ fontSize:14, fontWeight:900, color:ACCENT, fontFamily:"'JetBrains Mono',monospace" }}>28%</div>
            </div>
            <div style={{ height:3, background:"#1A1A1A", borderRadius:2, position:"relative", margin:"10px 0" }}>
              <div style={{ height:"100%", width:"28%", borderRadius:2, background:ACCENT }}/>
              <div style={{ width:12, height:12, borderRadius:"50%", background:ACCENT, position:"absolute", top:"50%", left:"28%", transform:"translateY(-50%)", boxShadow:`0 0 0 3px #0A0A0A` }}/>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"#252525", fontFamily:"'JetBrains Mono',monospace" }}><span>0%</span><span>50%</span><span>100%</span></div>
          </div>
          <div style={{ display:"flex", gap:6, marginBottom:8 }}>
            {[["LEAN","2041","#555","#1C1C1C","#0D0D0D"],["REGULAR ✦","2044",ACCENT,"rgba(191,255,0,0.3)","rgba(191,255,0,0.05)"],["FAT","2049","#7B9FFF","rgba(123,159,255,0.25)","rgba(123,159,255,0.05)"]].map(([label,yr,tc,bc,bg])=>(
              <div key={label} style={{ flex:1, textAlign:"center", padding:"8px 6px", borderRadius:8, fontSize:9, fontFamily:"'JetBrains Mono',monospace", border:`1px solid ${bc}`, background:bg, color:tc }}>
                <div style={{ fontSize:8, marginBottom:3, opacity:0.6 }}>{label}</div>
                <div style={{ fontSize:13 }}>{yr}</div>
              </div>
            ))}
          </div>
          <div style={{ background:"#111", border:"1px solid #1C1C1C", borderRadius:8, padding:"10px 14px", fontSize:10, color:"#444", fontFamily:"'JetBrains Mono',monospace", textAlign:"center" }}>
            At your current pace you retire in <span style={{ color:ACCENT }}>18 years</span> · age <span style={{ color:ACCENT }}>47</span>
          </div>
        </div>
      ),
    },
    // Panel 3: Bank Import
    {
      tag: "04 · IMPORT", tagColor: "#7B9FFF",
      title: <>Drop your bank PDF.<br/>Done in 30 seconds.</>,
      body: "Upload your bank's e-statement — HDFC, ICICI, SBI, Axis, Kotak and more. AI categorizes everything automatically. Raw PDF never leaves your browser.",
      bullets: [
        "Parsed entirely in-browser — PDF never uploaded to servers",
        "Password-protected PDFs supported",
        "AI auto-categorizes — review & edit before saving",
        "HDFC, ICICI, SBI, Axis, Kotak, most Indian e-statements",
      ],
      bulletColor: "#7B9FFF",
      mock: (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ border:"1px dashed #2A2A2A", borderRadius:10, padding:20, textAlign:"center", marginBottom:8 }}>
            <div style={{ fontSize:22, marginBottom:8, color:"#2A2A2A" }}>⬆</div>
            <div style={{ fontSize:11, color:"#444", marginBottom:4 }}>HDFC_Statement_May2026.pdf</div>
            <div style={{ display:"inline-block", padding:"3px 10px", background:"rgba(191,255,0,0.08)", border:"1px solid rgba(191,255,0,0.2)", borderRadius:20, fontSize:9, fontFamily:"'JetBrains Mono',monospace", color:ACCENT, marginTop:8 }}>
              ● PARSING IN BROWSER · 847 transactions found
            </div>
          </div>
          {[
            { emoji:"🍕", name:"Swiggy", cat:"FOOD", amt:"−₹680", amtColor:"#FF7C7C" },
            { emoji:"💹", name:"Zerodha · SIP", cat:"INVEST", amt:"+₹10,000", amtColor:"#6FE9B5" },
            { emoji:"🚗", name:"Ola", cat:"TRANSPORT", amt:"−₹240", amtColor:"#FF7C7C" },
            { emoji:"🏠", name:"Rent transfer", cat:"HOUSING", amt:"−₹22,000", amtColor:"#FF7C7C" },
          ].map((tx,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", background:"#111", border:"1px solid #1C1C1C", borderRadius:8, marginBottom:5 }}>
              <div style={{ width:26, height:26, borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, flexShrink:0, background:"rgba(255,124,124,0.1)", border:"1px solid rgba(255,124,124,0.2)" }}>{tx.emoji}</div>
              <div style={{ fontSize:11, color:"#888", flex:1 }}>{tx.name}</div>
              <div style={{ fontSize:9, color:"#2A2A2A", fontFamily:"'JetBrains Mono',monospace", marginRight:6 }}>{tx.cat}</div>
              <div style={{ fontSize:12, fontWeight:700, fontFamily:"'JetBrains Mono',monospace", color:tx.amtColor }}>{tx.amt}</div>
            </div>
          ))}
        </div>
      ),
    },
    // Panel 4: WhatsApp
    {
      tag: "05 · LOG", tagColor: "#6FE9B5",
    title: <>Your finances, inside<br/>actual WhatsApp chat</>,
    
    body: "Unbroke connects directly with WhatsApp so you can log expenses, review spending, and interact with your financial dashboard through real messages in real time.",
    
    bullets: [
      "Uses your real WhatsApp conversation — not a simulation",
      'Natural language support — "paid ₹1200 for electricity bill"',
      "AI categorizes and syncs transactions instantly",
      "Get monthly summaries, balances, and spending insights on demand",
    ],
      bulletColor: "#6FE9B5",
      mock: (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ background:"#0D0D0D", border:"1px solid #1C1C1C", borderRadius:"10px 10px 0 0", padding:"10px 14px", display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:28, height:28, borderRadius:"50%", background:"#1A3A1A", border:"1px solid #2A4A2A", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12 }}>🤖</div>
            <div>
              <div style={{ fontSize:11, color:OFF_WHITE, fontWeight:600 }}>Unbroke Bot</div>
              <div style={{ fontSize:9, color:"#2A5A2A", fontFamily:"'JetBrains Mono',monospace" }}>● ONLINE</div>
            </div>
          </div>
          <div style={{ background:"#080808", border:"1px solid #1C1C1C", borderTop:"none", borderRadius:"0 0 10px 10px", padding:14 }}>
            <div style={{ maxWidth:"80%", padding:"9px 12px", borderRadius:"12px 12px 2px 12px", marginBottom:6, fontSize:11, lineHeight:1.5, background:"#1A2A0A", border:"1px solid #2A3A10", color:ACCENT, marginLeft:"auto" }}>
              spent 250 on lunch at haldirams
            </div>
            <div style={{ maxWidth:"80%", padding:"9px 12px", borderRadius:"12px 12px 12px 2px", marginBottom:6, fontSize:11, lineHeight:1.5, background:"#111", border:"1px solid #1C1C1C", color:"#888" }}>
              Got it ✓
              <div style={{ background:"#0D1A00", border:"1px solid rgba(191,255,0,0.15)", borderRadius:8, padding:"10px 12px", marginTop:8, fontSize:10, fontFamily:"'JetBrains Mono',monospace", color:ACCENT }}>
                ₹250 · FOOD & DINING<br/>
                Haldirams · May 24, 2026<br/>
                <span style={{ color:"#6FE9B5" }}>→ saved to your dashboard</span>
              </div>
            </div>
            <div style={{ maxWidth:"80%", padding:"9px 12px", borderRadius:"12px 12px 2px 12px", marginBottom:6, fontSize:11, lineHeight:1.5, background:"#1A2A0A", border:"1px solid #2A3A10", color:ACCENT, marginLeft:"auto" }}>
              how much have i spent on food this month?
            </div>
            <div style={{ maxWidth:"80%", padding:"9px 12px", borderRadius:"12px 12px 12px 2px", fontSize:11, lineHeight:1.5, background:"#111", border:"1px solid #1C1C1C", color:"#888" }}>
              Food & Dining this month:
              <div style={{ background:"#0D1A00", border:"1px solid rgba(191,255,0,0.15)", borderRadius:8, padding:"10px 12px", marginTop:8, fontSize:10, fontFamily:"'JetBrains Mono',monospace", color:ACCENT }}>
                Total: <span style={{ color:"#FF7C7C" }}>₹8,240</span><br/>
                Budget: ₹7,000 · <span style={{ color:"#FFB561" }}>17% over</span><br/>
                Top: Swiggy ₹3,100 · Zomato ₹1,800
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Panel 5: Money Personality
    {
      tag: "06 · UNDERSTAND", tagColor: "#B098FF",
      title: <>Know your money<br/>personality archetype</>,
      body: "4 archetypes profiled from your actual spending data — not a quiz. See which behavioral traits dominate, where they help you, and where they cost you.",
      bullets: [
        "The Optimizer, The Experiencer, The Protector, The Builder",
        "Derived from your real data, not a questionnaire",
        "Trait breakdown across 6 behavioral dimensions",
        "Personalized blind spots and strengths report",
      ],
      bulletColor: "#B098FF",
      mock: (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ background:"#111", border:"1px solid rgba(176,152,255,0.2)", borderRadius:10, padding:16, marginBottom:8, textAlign:"center" }}>
            <div style={{ fontSize:8, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.2em", color:"#2A2A2A", marginBottom:8 }}>YOUR ARCHETYPE</div>
            <div style={{ fontSize:24, fontWeight:900, color:"#B098FF", letterSpacing:"-0.02em", marginBottom:4 }}>The Optimizer</div>
            <div style={{ fontSize:11, color:"#555", lineHeight:1.6 }}>Maximizes return-on-effort across all financial decisions. Strong savings consistency, but prone to analysis paralysis.</div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
            {[
              { label:"RISK TOLERANCE", pct:62, color:ACCENT },
              { label:"IMPULSE CONTROL", pct:84, color:"#6FE9B5" },
              { label:"LIFESTYLE SPEND", pct:41, color:"#FF7C7C" },
              { label:"FUTURE FOCUS", pct:77, color:"#7B9FFF" },
            ].map(t=>(
              <div key={t.label} style={{ border:"1px solid #2A2A2A", borderRadius:8, padding:"12px 10px", textAlign:"center", background:"#0D0D0D" }}>
                <div style={{ fontSize:9, color:"#444", fontFamily:"'JetBrains Mono',monospace", marginBottom:6 }}>{t.label}</div>
                <div style={{ height:3, borderRadius:2, background:`linear-gradient(to right,${t.color} ${t.pct}%,#1A1A1A ${t.pct}%)` }}/>
                <div style={{ fontSize:10, color:t.color, fontFamily:"'JetBrains Mono',monospace", marginTop:4 }}>{t.pct}%</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Panel 6: BIS Score
    {
      tag: "07 · SCORE", tagColor: ACCENT,
      title: <>A single score for your<br/>financial health</>,
      body: "The Behavioral Intelligence Score (0–100) is updated every month from your real data — savings rate, spending patterns, debt behavior, investment consistency.",
      bullets: [
        "Composite score across 6 behavioral dimensions",
        "Month-over-month trend with delta",
        "Breakdown by sub-score — see exactly what moved",
        "Actionable nudges tied to your lowest sub-score",
      ],
      bulletColor: ACCENT,
      mock: (
        <div style={{ width:"100%", maxWidth:320 }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}>
            <svg width="130" height="130" viewBox="0 0 130 130">
              <circle cx="65" cy="65" r="52" fill="none" stroke="#1A1A1A" strokeWidth="8"/>
              <circle cx="65" cy="65" r="52" fill="none" stroke={ACCENT} strokeWidth="8" strokeLinecap="round"
                strokeDasharray="327" strokeDashoffset="82" transform="rotate(-90 65 65)"
                style={{ filter:"drop-shadow(0 0 4px #BFFF00)" }}/>
              <text x="65" y="57" textAnchor="middle" fontSize="34" fill={OFF_WHITE} fontWeight="300">76</text>
              <text x="65" y="72" textAnchor="middle" fontSize="9" fill={ACCENT} fontFamily="monospace" letterSpacing="2">STRONG</text>
              <text x="65" y="86" textAnchor="middle" fontSize="9" fill="#333" fontFamily="monospace">↑ 4 pts vs last month</text>
            </svg>
          </div>
          {[
            { icon:"S", label:"Savings rate", pct:82, color:ACCENT },
            { icon:"I", label:"Investment consistency", pct:78, color:"#6FE9B5" },
            { icon:"L", label:"Lifestyle discipline", pct:61, color:"#FFB561" },
            { icon:"D", label:"Debt behaviour", pct:55, color:"#FF7C7C" },
          ].map((r,i)=>(
            <div key={i} style={{ display:"flex", gap:8, alignItems:"center", padding:"8px 12px", background:"#111", border:"1px solid #1C1C1C", borderRadius:8, marginBottom:5 }}>
              <div style={{ fontSize:10, width:20, height:20, borderRadius:5, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, background:`${r.color}18`, border:`1px solid ${r.color}35`, color:r.color }}>{r.icon}</div>
              <div style={{ fontSize:11, color:"#666", flex:1 }}>{r.label}</div>
              <div style={{ width:80, height:3, background:"#1A1A1A", borderRadius:2, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${r.pct}%`, background:r.color, borderRadius:2 }}/>
              </div>
              <div style={{ fontSize:10, fontFamily:"'JetBrains Mono',monospace", color:r.color }}>{r.pct}</div>
            </div>
          ))}
          <div style={{ marginTop:8, padding:"10px 14px", background:"#111", border:"1px solid rgba(191,255,0,0.15)", borderRadius:8, fontSize:10, color:"#555" }}>
            <span style={{ color:ACCENT }}>↳ Nudge: </span>Reducing lifestyle spend by 8% would push your score to <span style={{ color:ACCENT }}>83 (Elite)</span>
          </div>
        </div>
      ),
    },
  ];

  const p = panels[activeTab];

  return (
    <section style={{ padding: "0 48px 100px", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 56 }}>
        <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.22em", color: "#333", marginBottom: 18 }}>FEATURES IN DEPTH</div>
        <h2 style={{ fontSize: "clamp(30px, 4.5vw, 52px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.08 }}>
          Every tool you need.<br/><span style={{ color: ACCENT }}>Nothing you don't.</span>
        </h2>
      </div>

      {/* Tab nav */}
      <div style={{ display:"flex", gap:2, marginBottom:2, border:`1px solid ${BORDER}`, borderRadius:"14px 14px 0 0", overflow:"hidden" }}>
        {TABS.map((tab, i) => (
          <button key={i} onClick={() => setActiveTab(i)} style={{
            flex:1, padding:"14px 10px", background: i===activeTab ? "#111" : "#0A0A0A",
            border:"none", color: i===activeTab ? ACCENT : "#333",
            fontSize:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.1em",
            cursor:"pointer", transition:"all 0.2s",
            borderRight: i < TABS.length-1 ? `1px solid ${BORDER}` : "none",
            textAlign:"center",
          }}>
            {i===activeTab && <span style={{ display:"inline-block", width:5, height:5, borderRadius:"50%", background:ACCENT, marginRight:6, verticalAlign:"middle" }}/>}
            {tab}
          </button>
        ))}
      </div>

      {/* Panel */}
      <div style={{ border:`1px solid ${BORDER}`, borderTop:"none", borderRadius:"0 0 14px 14px", background:"#0A0A0A", overflow:"hidden", display:"grid", gridTemplateColumns:"1fr 1fr" }}>
        <div style={{ padding:"44px 40px", borderRight:`1px solid ${BORDER}` }}>
          <div style={{ display:"inline-block", padding:"3px 10px", borderRadius:20, fontSize:9, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.14em", marginBottom:20, background:`${p.tagColor}14`, border:`1px solid ${p.tagColor}35`, color:p.tagColor }}>{p.tag}</div>
          <h3 style={{ fontSize:"clamp(20px, 2.5vw, 28px)", fontWeight:900, letterSpacing:"-0.02em", lineHeight:1.2, marginBottom:14 }}>{p.title}</h3>
          <p style={{ fontSize:13, color:MUTED, lineHeight:1.8, marginBottom:24 }}>{p.body}</p>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {p.bullets.map((b,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                <div style={{ width:18, height:18, borderRadius:5, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, flexShrink:0, marginTop:1, background:`${p.bulletColor}15`, border:`1px solid ${p.bulletColor}30`, color:p.bulletColor }}>✓</div>
                <div style={{ fontSize:12, color:"#666", lineHeight:1.6 }}>{b}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background:"#080808", padding:32, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {p.mock}
        </div>
      </div>

      {/* Secondary features */}
      <div style={{ marginTop:2, border:`1px solid ${BORDER}`, borderTop:"none", borderRadius:"0 0 14px 14px", padding:"28px 28px 24px", background:"#080808" }}>
        <div style={{ fontSize:9, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.2em", color:"#252525", marginBottom:14 }}>ALSO INCLUDED</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
          {[
            { icon:"◎", title:"Debt Tracker", desc:"Payoff scheduler with interest math. See your exact debt-free date per loan." },
            { icon:"◑", title:"Regret Tracker", desc:"Review discretionary purchases. Flag regrets. Build spending self-awareness." },
            { icon:"◐", title:"Strategic Freedom Map", desc:"5-question framework to map your path from income to financial independence." },
            { icon:"⊕", title:"Monthly Reports", desc:"Full behavioral report emailed monthly — patterns, score delta, top actions." },
          ].map((c,i)=>(
            <div key={i} style={{ border:`1px solid ${BORDER}`, borderRadius:12, padding:20, background:"#0A0A0A", cursor:"pointer", transition:"all 0.2s" }}
              onMouseEnter={e=>{ e.currentTarget.style.background="#0D0D0D"; e.currentTarget.style.borderColor="#2A2A2A"; e.currentTarget.style.transform="translateY(-2px)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.background="#0A0A0A"; e.currentTarget.style.borderColor=BORDER; e.currentTarget.style.transform="none"; }}>
              <div style={{ fontSize:18, color:"#2A2A2A", marginBottom:12 }}>{c.icon}</div>
              <div style={{ fontSize:12, fontWeight:700, color:"#444", marginBottom:6, letterSpacing:"-0.01em" }}>{c.title}</div>
              <div style={{ fontSize:11, color:"#2A2A2A", lineHeight:1.6 }}>{c.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Main export ───────────────────────────────────────────────────────────────
export default function UnbrokeLanding({ onGetStarted, onSignIn, onTryDemo }) {
  useSEO();
  const [scrolled, setScrolled]       = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const t = setInterval(() => setActiveTestimonial(i => (i + 1) % TESTIMONIALS.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: DARK,
      color: OFF_WHITE,
      fontFamily: "'Geist', 'Outfit', 'Helvetica Neue', sans-serif",
      overflowX: "hidden",
    }}>

      {/* ── Styles ─────────────────────────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;700;900&family=JetBrains+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: ${ACCENT}; color: ${DARK}; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #222; border-radius: 4px; }

        @keyframes ub-fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ub-blink { 0%,100% { opacity:1; } 50% { opacity:0; } }
        @keyframes ub-ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }

        .ub-fu  { animation: ub-fadeUp 0.85s cubic-bezier(0.16,1,0.3,1) both; }
        .ub-d1  { animation-delay: 0.05s; }
        .ub-d2  { animation-delay: 0.18s; }
        .ub-d3  { animation-delay: 0.30s; }
        .ub-d4  { animation-delay: 0.44s; }
        .ub-d5  { animation-delay: 0.58s; }

        .ub-primary {
          padding: 16px 38px;
          background: ${ACCENT};
          border: none; border-radius: 10px;
          color: ${DARK}; font-size: 15px; font-weight: 700;
          cursor: pointer; font-family: inherit; letter-spacing: -0.01em;
          transition: transform 0.15s, box-shadow 0.15s;
          display: inline-block;
        }
        .ub-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 44px ${ACCENT}35; }

        .ub-ghost {
          padding: 16px 38px;
          background: transparent; border: 1px solid #222;
          border-radius: 10px; color: #666; font-size: 15px;
          cursor: pointer; font-family: inherit;
          transition: border-color 0.15s, color 0.15s;
          display: inline-block;
        }
        .ub-ghost:hover { border-color: #444; color: ${OFF_WHITE}; }

        .ub-feat:hover { background: #111 !important; }
        .ub-tier { border: 1px solid ${BORDER}; border-radius: 14px; padding: 28px 24px; background: ${DARK}; position: relative; transition: transform 0.2s; }
        .ub-tier:hover { transform: translateY(-4px); }
        .ub-tier.rec { border-color: ${ACCENT}55; background: #0D0D0D; }

        .ub-pain:hover .ub-pain-icon { color: ${ACCENT} !important; }
        .ub-pain:hover { background: #0D0D0D !important; border-color: ${ACCENT}30 !important; }

        .ub-cmp-row:hover { background: #111 !important; }

        .ub-social-num { transition: transform 0.3s; }
        .ub-social-num:hover { transform: translateY(-3px); }

        @media (max-width: 768px) {
          .ub-grid3 { grid-template-columns: 1fr !important; }
          .ub-grid2 { grid-template-columns: 1fr !important; }
          .ub-hide-mobile { display: none !important; }
          .ub-hero-h1 { font-size: 52px !important; }
        }
      `}</style>

      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 50,
        padding: "14px 48px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderBottom: scrolled ? `1px solid ${BORDER}` : "1px solid transparent",
        background: scrolled ? "rgba(10,10,10,0.96)" : "transparent",
        backdropFilter: scrolled ? "blur(14px)" : "none",
        transition: "all 0.25s",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7, background: ACCENT,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 900, fontSize: 15, color: DARK, letterSpacing: "-0.04em",
          }}>u</div>
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.03em" }}>unbroke</span>
          <span style={{
            marginLeft: 4, padding: "2px 8px",
            background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`,
            borderRadius: 20, fontSize: 9, color: ACCENT,
            fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em",
          }}>FREE</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onTryDemo && (
            <button onClick={onTryDemo} style={{
              padding: "8px 18px", background: "transparent",
              border: `1px solid ${BORDER}`, borderRadius: 8,
              color: "#666", fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              transition: "all 0.15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#444"; e.currentTarget.style.color = OFF_WHITE; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = "#666"; }}>
              Live demo
            </button>
          )}
          {onSignIn && (
            <button onClick={onSignIn} style={{
              padding: "8px 20px", background: ACCENT,
              border: `1px solid ${ACCENT}`, borderRadius: 8,
              color: DARK, fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
            }}>Sign in →</button>
          )}
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section style={{ padding: "96px 48px 0", maxWidth: 1100, margin: "0 auto" }}>
        {/* Badge */}
        <div className="ub-fu" style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "5px 14px", border: `1px solid ${ACCENT}30`,
          background: `${ACCENT}0D`, borderRadius: 100,
          marginBottom: 36, fontSize: 10, color: ACCENT,
          fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.16em",
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: ACCENT, animation: "ub-blink 2s ease-in-out infinite" }}/>
          FREE FOREVER · NO CREDIT CARD · 5-MINUTE SETUP
        </div>

        {/* H1 — keyword-rich for SEO */}
        <h1 className="ub-fu ub-d1 ub-hero-h1" style={{
          fontSize: "clamp(52px, 8.5vw, 100px)",
          fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.93, marginBottom: 32,
        }}>
          Stop guessing<br/>
          where it all <span style={{ color: ACCENT }}>went.</span>
        </h1>

        {/* SEO-rich subhead */}
        <p className="ub-fu ub-d2" style={{
          fontSize: 18, color: "#666", maxWidth: 520, lineHeight: 1.75, marginBottom: 16, fontWeight: 300,
        }}>
          Unbroke is a free personal finance tracker for India and beyond. Track income, expenses and investments. Get AI behavioral analysis. See your FIRE timeline. No spreadsheet. No monthly fee.
        </p>

        {/* Trust nudge */}
        <div className="ub-fu ub-d2" style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 40,
          fontSize: 12, color: "#444",
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          <div style={{ display: "flex", gap: -6 }}>
            {["PM","RS","AK","VT","NK"].map((init, i) => (
              <div key={i} style={{
                width: 26, height: 26, borderRadius: "50%",
                background: ["#7B9FFF","#BFFF00","#FF8E7C","#6FE9B5","#B098FF"][i],
                border: `2px solid ${DARK}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 8, fontWeight: 700, color: DARK,
                marginLeft: i > 0 ? -8 : 0,
              }}>{init}</div>
            ))}
          </div>
          <span>12,400+ people tracking their wealth · 4.9★</span>
        </div>

        {/* CTAs */}
        <div className="ub-fu ub-d3" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button className="ub-primary" onClick={onGetStarted}>Start free — 2 minutes →</button>
          {onTryDemo && <button className="ub-ghost" onClick={onTryDemo}>Explore demo first</button>}
        </div>

        {/* Stats strip */}
        <div className="ub-fu ub-d4" style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1, marginTop: 80, border: `1px solid ${BORDER}`,
          borderRadius: 14, overflow: "hidden",
        }}>
          {HERO_STATS.map((s, i) => (
            <div key={i} style={{
              padding: "24px 20px", background: i % 2 === 0 ? "#0D0D0D" : "#0A0A0A",
              textAlign: "center", borderRight: i < 3 ? `1px solid ${BORDER}` : "none",
            }}>
              <div style={{ fontSize: 34, fontWeight: 900, color: ACCENT, letterSpacing: "-0.03em", fontFamily: "'JetBrains Mono', monospace" }}>{s.val}</div>
              <div style={{ fontSize: 10, color: "#444", marginTop: 5, fontFamily: "'JetBrains Mono', monospace" }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── App Preview ─────────────────────────────────────────────────────── */}
      <AppPreview/>

      {/* ── Ticker ──────────────────────────────────────────────────────────── */}
      <div style={{ overflow: "hidden", borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: "14px 0", margin: "80px 0 0" }}>
        <div style={{ display: "flex", animation: "ub-ticker 26s linear infinite", whiteSpace: "nowrap" }}>
          {[...Array(2)].map((_, ri) => (
            <span key={ri} style={{ display: "flex" }}>
              {TICKER.map((t, i) => (
                <span key={i} style={{ padding: "0 28px", fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.22em", color: t === "·" ? ACCENT : "#666" }}>{t}</span>
              ))}
            </span>
          ))}
        </div>
      </div>
{/* ── AI Behavioral Pattern Cards ─────────────────────────────── */}
<section style={{ padding: "100px 48px", maxWidth: 1100, margin: "0 auto" }}>
  <div style={{ fontSize:10, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.22em", color:"#666", marginBottom:18 }}>AI BEHAVIORAL PATTERNS</div>
  <h2 style={{ fontSize:"clamp(30px,4.5vw,52px)", fontWeight:900, letterSpacing:"-0.03em", lineHeight:1.08, marginBottom:16 }}>
    Your habits,{" "}<span style={{ color:ACCENT }}>decoded.</span>
  </h2>
  <p style={{ fontSize:15, color:MUTED, maxWidth:540, lineHeight:1.75, marginBottom:48 }}>
    After your first month of data, Unbroke's AI reads your actual transactions and surfaces the patterns driving your behavior — not generic tips, specific observations anchored to your numbers.
  </p>

  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12, marginBottom:20 }}>
    {[
      { icon:"📈", stat:"+18%", statLabel:"income growth", title:"Your spending is rising in lockstep with your income", body:"Income grew 18% over 3 months while savings rate held flat at 6%.", badge:"⚠ FLAG · LIFESTYLE CREEP", badgeColor:"#C05A5A" },
      { icon:"🌀", stat:"Fri–Sat", statLabel:"peak spend days", title:"You order Swiggy mostly on Friday nights after 9 PM", body:"8 of your last 11 food orders landed between 9–11 PM on weekends.", badge:"◎ HABIT · EMOTIONAL SPENDING", badgeColor:"#E8894A" },
      { icon:"💧", stat:"31%", statLabel:"uncategorized", title:'Nearly a third of your expenses are "Other"', body:"31% uncategorized is a classic financial leakage signal — small unknowns that compound.", badge:"⚠ FLAG · FINANCIAL LEAKAGE", badgeColor:"#4EAD8A" },
      { icon:"〰️", stat:"68%", statLabel:"month-to-month swing", title:"Your savings contributions swing wildly month to month", body:"Wealth-building varies by 68% — reactive, not systematic. One skipped month in Oct.", badge:"◎ HABIT · INCONSISTENT SAVING", badgeColor:"#9B7ECF" },
      { icon:"💼", stat:"8.2%", statLabel:"wealth rate", title:"Your income supports far more growth than your structure reflects", body:"Avg income ₹85,000/mo but only 8.2% routed to wealth building.", badge:"⚠ FLAG · UNDERUTILIZED INCOME", badgeColor:"#5A8EC0" },
      { icon:"🏆", stat:"top 22%", statLabel:"discipline rank", title:"Your Housing-to-income ratio is impressively disciplined", body:"You spend 26% on housing vs. a 35% benchmark. That gap compounds in your favour.", badge:"✦ STRENGTH · FIXED OBLIGATIONS", badgeColor:"#4EAD8A" },
    ].map((card, i) => (
      <div key={i} style={{ background:"#0D0D0D", border:`1px solid ${BORDER}`, borderLeft:`3px solid ${card.badgeColor}`, borderRadius:14, padding:"22px 20px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
          <span style={{ fontSize:24 }}>{card.icon}</span>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:18, fontWeight:700, color:card.badgeColor, fontFamily:"'JetBrains Mono',monospace" }}>{card.stat}</div>
            <div style={{ fontSize:9, color:"#333", fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.1em" }}>{card.statLabel.toUpperCase()}</div>
          </div>
        </div>
        <div style={{ fontSize:13, fontWeight:600, color:OFF_WHITE, marginBottom:8, lineHeight:1.4 }}>{card.title}</div>
        <div style={{ fontSize:12, color:MUTED, lineHeight:1.65, marginBottom:12 }}>{card.body}</div>
        <div style={{ fontSize:9, color:card.badgeColor, fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.12em", padding:"3px 8px", borderRadius:20, display:"inline-block", background:`${card.badgeColor}18`, border:`1px solid ${card.badgeColor}30` }}>{card.badge}</div>
      </div>
    ))}
  </div>

  <div style={{ textAlign:"center", fontSize:10, color:"#555", fontFamily:"'JetBrains Mono',monospace", letterSpacing:"0.1em", padding:"10px", border:`1px dashed ${BORDER}`, borderRadius:10 }}>
    ↻ REFRESHES AUTOMATICALLY EACH MONTH · POWERED BY GPT-4o
  </div>
</section>
      {/* ── Pain Points ─────────────────────────────────────────────────────── */}
      <section style={{ padding: "100px 48px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.22em", color: "#333", marginBottom: 18 }}>THE PROBLEM</div>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 48px)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.1 }}>
            Most people earning well<br/>
            still feel <span style={{ color: ACCENT }}>broke.</span>
          </h2>
          <p style={{ fontSize: 15, color: MUTED, marginTop: 16, maxWidth: 540, lineHeight: 1.75 }}>
            It's not the income. It's the invisible patterns — lifestyle creep, emotional spending, savings inconsistency — that compound silently while you're busy working.
          </p>
        </div>

        <div className="ub-grid3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {PAIN_POINTS.map((p, i) => (
            <div key={i} className="ub-pain" style={{
              padding: "32px 28px",
              background: "#0A0A0A",
              border: `1px solid ${BORDER}`,
              borderRadius: 14,
              transition: "all 0.2s",
              cursor: "default",
            }}>
              <div className="ub-pain-icon" style={{ fontSize: 28, color: "#2A2A2A", marginBottom: 20, transition: "color 0.2s" }}>{p.icon}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: OFF_WHITE, marginBottom: 12, letterSpacing: "-0.01em", lineHeight: 1.35 }}>{p.problem}</div>
              <div style={{ width: 28, height: 1, background: ACCENT, marginBottom: 14 }}/>
              <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.75 }}>{p.solution}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Social Proof Numbers ─────────────────────────────────────────────── */}
      <section style={{ padding: "0 48px 100px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "20px 32px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT, animation: "ub-blink 2s ease-in-out infinite" }}/>
            <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.2em", color: ACCENT }}>LIVE PLATFORM STATS</span>
          </div>
          <div className="ub-grid2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)" }}>
            {SOCIAL_PROOF_NUMBERS.map((s, i) => (
              <div key={i} className="ub-social-num" style={{
                padding: "36px 28px", textAlign: "center",
                borderRight: i < 3 ? `1px solid ${BORDER}` : "none",
              }}>
                <div style={{ fontSize: 36, fontWeight: 900, color: OFF_WHITE, letterSpacing: "-0.03em", fontFamily: "'JetBrains Mono', monospace", marginBottom: 8 }}>
                  <CountUp target={s.val}
                    prefix={s.val.startsWith("₹") ? "₹" : s.val.startsWith("4") ? "" : ""}
                    suffix={s.val.endsWith("+") ? "+" : s.val.endsWith("★") ? "★" : s.val.endsWith("%") ? "%" : ""}
                  />
                </div>
                <div style={{ fontSize: 11, color: "#444", lineHeight: 1.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature Showcase (Tabbed) ────────────────────────────────────────── */}
      <FeatureShowcase />

      {/* ── Testimonials ────────────────────────────────────────────────────── */}
      <section style={{ padding: "0 48px 100px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 52 }}>
          <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.22em", color: "#333", marginBottom: 18 }}>WHAT PEOPLE SAY</div>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 46px)", fontWeight: 900, letterSpacing: "-0.03em" }}>
            Real users. <span style={{ color: ACCENT }}>Real shifts.</span>
          </h2>
        </div>

        {/* Featured testimonial */}
        <div style={{ marginBottom: 20, position: "relative" }}>
          {TESTIMONIALS.map((t, i) => (
            <div key={i} style={{
              display: i === activeTestimonial ? "block" : "none",
              background: "#0D0D0D", border: `1px solid ${BORDER}`,
              borderLeft: `3px solid ${t.color}`,
              borderRadius: 14, padding: "36px 40px",
              animation: "ub-fadeUp 0.4s ease both",
            }}>
              <div style={{ fontSize: 28, color: t.color, marginBottom: 20, opacity: 0.4, fontFamily: "serif", lineHeight: 1 }}>"</div>
              <p style={{ fontSize: 18, color: OFF_WHITE, lineHeight: 1.7, fontWeight: 300, letterSpacing: "-0.01em", marginBottom: 24, maxWidth: 700 }}>
                {t.quote}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 42, height: 42, borderRadius: "50%", background: `${t.color}25`, border: `2px solid ${t.color}50`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: t.color }}>
                  {t.initials}
                </div>
                <div>
                  <div style={{ fontSize: 13, color: OFF_WHITE, fontWeight: 500 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: MUTED }}>{t.role}</div>
                </div>
                <div style={{ marginLeft: "auto", color: "#FFB561", fontSize: 12, letterSpacing: 2 }}>★★★★★</div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial dots */}
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 32 }}>
          {TESTIMONIALS.map((t, i) => (
            <button key={i} onClick={() => setActiveTestimonial(i)} style={{
              width: i === activeTestimonial ? 24 : 8, height: 8,
              borderRadius: 4, border: "none", cursor: "pointer",
              background: i === activeTestimonial ? ACCENT : "#222",
              transition: "all 0.3s",
            }}/>
          ))}
        </div>

        {/* Mini testimonial grid */}
        <div className="ub-grid2" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
          {TESTIMONIALS.map((t, i) => (
            <div key={i} onClick={() => setActiveTestimonial(i)} style={{
              background: i === activeTestimonial ? "#0D0D0D" : "#080808",
              border: `1px solid ${i === activeTestimonial ? `${t.color}40` : BORDER}`,
              borderRadius: 12, padding: "18px 20px", cursor: "pointer", transition: "all 0.2s",
            }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${t.color}20`, border: `1px solid ${t.color}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: t.color }}>{t.initials}</div>
                <div>
                  <div style={{ fontSize: 12, color: OFF_WHITE, fontWeight: 500 }}>{t.name}</div>
                  <div style={{ fontSize: 10, color: MUTED }}>{t.role}</div>
                </div>
                <div style={{ marginLeft: "auto", color: "#FFB561", fontSize: 10 }}>★★★★★</div>
              </div>
              <p style={{ fontSize: 12, color: MUTED, lineHeight: 1.65 }}>"{t.quote.slice(0, 90)}…"</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Comparison Table ─────────────────────────────────────────────────── */}
      <section style={{ padding: "0 48px 100px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 52 }}>
          <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.22em", color: "#333", marginBottom: 18 }}>HOW WE COMPARE</div>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 46px)", fontWeight: 900, letterSpacing: "-0.03em" }}>
            Unbroke vs. the <span style={{ color: ACCENT }}>alternatives.</span>
          </h2>
        </div>

        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 16, overflow: "hidden" }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", background: "#0D0D0D", borderBottom: `1px solid ${BORDER}`, padding: "16px 24px" }}>
            <div style={{ fontSize: 11, color: MUTED }}>Feature</div>
            {[["Unbroke", ACCENT], ["Spreadsheet", "#555"], ["Others", "#555"]].map(([label, color]) => (
              <div key={label} style={{ fontSize: 11, color, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textAlign: "center" }}>{label.toUpperCase()}</div>
            ))}
          </div>

          {COMPARISON.map((row, i) => (
            <div key={i} className="ub-cmp-row" style={{
              display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr",
              padding: "14px 24px", borderBottom: i < COMPARISON.length - 1 ? `1px solid ${BORDER}` : "none",
              background: "#0A0A0A", alignItems: "center", transition: "background 0.15s",
            }}>
              <div style={{ fontSize: 13, color: "#888" }}>{row.feature}</div>
              {[row.unbroke, row.spreadsheet, row.competitors].map((val, j) => (
                <div key={j} style={{ textAlign: "center" }}>
                  {val === true  && <span style={{ color: ACCENT, fontSize: 15 }}>✓</span>}
                  {val === false && <span style={{ color: "#2A2A2A", fontSize: 15 }}>✗</span>}
                  {val === "paid" && <span style={{ fontSize: 10, color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>PAID</span>}
                  {val === "diy"  && <span style={{ fontSize: 10, color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>DIY</span>}
                  {typeof val === "string" && !["paid","diy"].includes(val) && (
                    <span style={{ fontSize: 11, color: j === 0 ? ACCENT : "#555", fontFamily: "'JetBrains Mono', monospace" }}>{val}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────────────── */}
      <section style={{ padding: "0 48px 100px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 56 }}>
          <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.22em", color: "#333", marginBottom: 18 }}>PLANS</div>
          <h2 style={{ fontSize: "clamp(30px, 4.5vw, 52px)", fontWeight: 900, letterSpacing: "-0.03em" }}>
            Free forever.<br/>Upgrade only for <span style={{ color: ACCENT }}>AI.</span>
          </h2>
          <p style={{ fontSize: 14, color: MUTED, marginTop: 16, maxWidth: 460, lineHeight: 1.75 }}>
            The Free plan covers the essentials — no expiry, no card. Growth and Elite unlock AI behavioral analysis and the wealth assistant.
          </p>
        </div>

        <div className="ub-grid3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {TIERS.map(t => (
            <div key={t.id} className={`ub-tier${t.recommended ? " rec" : ""}`}>
              {t.recommended && (
                <div style={{
                  position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
                  background: ACCENT, color: DARK, padding: "4px 16px", borderRadius: 100,
                  fontSize: 9, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: "0.14em", whiteSpace: "nowrap",
                }}>{t.label}</div>
              )}
              {!t.recommended && (
                <div style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", color: "#333", marginBottom: 10 }}>{t.label}</div>
              )}
              {t.recommended && <div style={{ marginBottom: 10 }}/>}
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{t.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 28 }}>
                <span style={{ fontSize: 46, fontWeight: 900, color: t.recommended ? ACCENT : OFF_WHITE, letterSpacing: "-0.04em", fontFamily: "'JetBrains Mono', monospace" }}>{t.price === 0 ? "₹0" : `${t.price}`}</span>
                <span style={{ fontSize: 12, color: "#333" }}>{t.price === 0 ? "forever" : "/month"}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 11, marginBottom: 30 }}>
                {t.features.map(f => (
                  <div key={f} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ color: ACCENT, fontSize: 12, marginTop: 1, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 12, color: "#555", lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => t.id === 0 ? onGetStarted?.() : window.open(PAYMENT_LINKS[t.id], "_blank")}
                style={{
                  width: "100%", padding: "13px",
                  background: t.recommended ? ACCENT : "transparent",
                  border: `1px solid ${t.recommended ? ACCENT : "#222"}`,
                  borderRadius: 10, color: t.recommended ? DARK : "#555",
                  fontSize: 13, fontWeight: t.recommended ? 700 : 400,
                  cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                }}
                onMouseEnter={e => { if (!t.recommended) { e.currentTarget.style.borderColor = "#444"; e.currentTarget.style.color = OFF_WHITE; }}}
                onMouseLeave={e => { if (!t.recommended) { e.currentTarget.style.borderColor = "#222"; e.currentTarget.style.color = "#555"; }}}>
                {t.id === 0 ? "Start free →" : `Get ${t.name} →`}
              </button>
            </div>
          ))}
        </div>
        <p style={{ textAlign: "center", marginTop: 22, fontSize: 10, color: "#555", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
          Secure checkout via Razorpay · No lock-in · Cancel anytime
        </p>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <section style={{ padding: "0 48px 100px", maxWidth: 800, margin: "0 auto" }}>
        <div style={{ marginBottom: 52 }}>
          <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.22em", color: "#333", marginBottom: 18 }}>FAQ</div>
          <h2 style={{ fontSize: "clamp(28px, 4vw, 46px)", fontWeight: 900, letterSpacing: "-0.03em" }}>
            Questions people<br/><span style={{ color: ACCENT }}>actually ask.</span>
          </h2>
        </div>
        <div>
          {FAQS.map((f, i) => <FAQ key={i} q={f.q} a={f.a}/>)}
        </div>
      </section>

      {/* ── Bottom CTA ──────────────────────────────────────────────────────── */}
      <section style={{ padding: "80px 48px 100px", maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
        <div style={{ width: 40, height: 2, background: ACCENT, margin: "0 auto 36px", borderRadius: 2 }}/>
        <h2 style={{ fontSize: "clamp(40px, 6.5vw, 76px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.95, marginBottom: 24 }}>
          Your money has<br/>
          a <span style={{ color: ACCENT }}>story.</span><br/>
          <span style={{ WebkitTextStroke: `2px ${OFF_WHITE}`, color: "transparent" }}>Read it.</span>
        </h2>
        <p style={{ fontSize: 16, color: "#555", marginBottom: 48, lineHeight: 1.7 }}>
          Free forever. 5 minutes setup. No spreadsheets. No guilt.
        </p>
        <button className="ub-primary" onClick={onGetStarted} style={{ fontSize: 16, fontWeight: 900, padding: "20px 52px" }}>
          Start for free →
        </button>
        <div style={{ marginTop: 16, fontSize: 11, color: "#333", fontFamily: "'JetBrains Mono', monospace" }}>
          Join 12,400+ people who finally know where it all went.
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer style={{ padding: "32px 48px", borderTop: `1px solid ${BORDER}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 24, marginBottom: 24 }}>
          {/* Brand */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: ACCENT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: DARK }}>u</div>
              <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.02em" }}>unbroke</span>
            </div>
            <div style={{ fontSize: 11, color: "#333", maxWidth: 200, lineHeight: 1.6 }}>
              AI-powered financial intelligence. Free forever.
            </div>
          </div>

          {/* Links */}
          <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
            {[
              { heading: "Product", links: ["Features", "Pricing", "Demo", "Changelog"] },
              { heading: "Resources", links: ["FIRE Calculator", "Debt Tracker", "AI Analysis", "Bank Import"] },
              { heading: "Company", links: ["About", "Privacy Policy", "Terms", "Contact"] },
            ].map(col => (
              <div key={col.heading}>
                <div style={{ fontSize: 10, color: "#333", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em", marginBottom: 12 }}>{col.heading.toUpperCase()}</div>
                {col.links.map(l => (
                  <div key={l} style={{ fontSize: 12, color: "#444", marginBottom: 8, cursor: "pointer", transition: "color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = OFF_WHITE}
                    onMouseLeave={e => e.currentTarget.style.color = "#444"}>{l}</div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div style={{ fontSize: 10, color: "#555", fontFamily: "'JetBrains Mono', monospace" }}>
            © {new Date().getFullYear()} Unbroke · formerly Purely Automated · All rights reserved
          </div>
          <div style={{ fontSize: 10, color: "#3A3A3A", fontFamily: "'JetBrains Mono', monospace" }}>v2.0 · Built with ♥ in India</div>
        </div>
      </footer>
    </div>
  );
        }
